import cx from 'classnames'
import { useCollabItemContext } from 'components/CollabItem/CollabItem'
import { Item } from 'components/CollabItem/Item'
import { IconButton } from 'components/IconButton/IconButton'
import { useTodoPermissions } from '../useTodoPermissions'
import { observer } from 'mobx-react-lite'
import { Key, useState, useEffect } from 'react'
import { useRootStore } from 'stores/RootStore'
import { ReactComponent as ThreeDotsIcon } from 'assets/icons/threeDots.svg'
import { Item as MenuItem } from '@react-stately/collections'
import { Menu } from 'components/Menu/Menu'
import { ReactComponent as EditIcon } from 'assets/icons/edit16.svg'
import { ReactComponent as TrashIcon } from 'assets/icons/trash16.svg'
import Styles from './TodoItem.module.scss'
import { TodoItemProps } from './Types'
import Avatar from 'common/Avatar/Avatar'
import { DueDatePicker } from 'components/DueDatePicker/DueDatePicker'
import { TodoItemEditForm } from '../TodoItemEditForm/TodoItemEditForm'
import { ResolveControl } from '../ResolveControl/ResolveControl'
import { TodoAssigneePicker } from 'components/TodoAssigneePicker/TodoAssigneePicker'
import { ProseDisplay } from 'common/ProseInput/ProseDisplay'
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar16.svg'
import { ReactComponent as ResolveIcon } from 'assets/icons/resolve.svg'
import { ReactComponent as RestoreIcon } from 'assets/icons/reset16.svg'
import dayjs from 'dayjs'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { getInitials } from 'utils'
import { useAuth } from 'hooks/UseAuth'
import { TodoCheckbox } from 'common/Prose/nodeViews/TodoListItem/TodoCheckbox/TodoCheckbox'

const TodoItem = observer(({ todo, inCleanupMode = false, hasReplies = false }: TodoItemProps) => {
  const { setEditing, editing, isActive } = useCollabItemContext()
  const { canEdit, canDelete, canToggleResolve, canChangeDueDate, canAssign } = useTodoPermissions(todo, inCleanupMode)
  const isEditing = editing?.type === 'todo' && editing?.id === todo.id
  const showActionMenu = (canEdit || canDelete || canToggleResolve) && !inCleanupMode
  const rootStore = useRootStore()
  const [initialEditFocus, setInitialEditFocus] = useState<'title' | 'description'>('title')
  const createdOn = dayjs(todo.created_at).format('MMM D')
  const showUpperFooter = (isActive && (canAssign || canChangeDueDate)) || todo.assignee || todo.due_date
  const showLowerFooter = isActive && todo.description
  const auth = useAuth()

  const disabledKeys = new Set<string>()
  if (!canEdit) disabledKeys.add('edit')
  if (!canToggleResolve) disabledKeys.add('resolve')
  if (!canDelete) disabledKeys.add('delete')

  const editTitle = () => {
    setEditing({ type: 'todo', id: todo.id })
    rootStore.todos.select(todo.id)
  }

  const handleAction = (action: Key) => {
    switch (action) {
      case 'edit':
        editTitle()
        break
      case 'delete':
        rootStore.todos.delete(todo.id, { trackArticleUpdate: true })
        break
      case 'resolve':
        rootStore.todos.toggleCompleted(todo.id)
        break
    }
  }

  useEffect(() => {
    if (!isEditing) {
      setInitialEditFocus('title')
    }
  }, [isEditing])

  useEffect(() => {
    if (!isActive) {
      setEditing(undefined)
    }
  }, [isActive]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCheckboxClick = () => {
    // wait 400ms for checkbox animation
    setTimeout(() => {
      if (!!auth) rootStore.todos.toggleCompleted(todo.id)
    }, 400)
  }

  return (
    <Item.Root
      editing={isEditing}
      className={cx(Styles.todoItem, Styles.root, {
        [Styles.hasReplies]: hasReplies && !isEditing,
        [Styles.isEditing]: isEditing,
        [Styles.isActive]: isActive,
      })}
    >
      {!isEditing && (
        <div className={Styles.controlGroupContainer}>
          <Item.ControlGroup>
            {showActionMenu && (
              <Item.Control tooltipTitle="More options">
                <DropdownTrigger placement="bottom-end" popoverClassName={Styles.dropdown}>
                  <IconButton icon={ThreeDotsIcon} aria-label="More actions" className={Styles.controlItem} />
                  {(close) => (
                    <>
                      <div className={Styles.creator}>
                        <Avatar
                          size={20}
                          url={todo.creator.avatar_url}
                          label={getInitials(todo.creator.display_name)}
                          className={Styles.creatorAvatar}
                        />
                        <span className={Styles.creatorLabel}>
                          Created by {todo.creator.display_name} on {createdOn}
                        </span>
                      </div>
                      <Menu aria-label="More actions" onAction={handleAction} disabledKeys={disabledKeys}>
                        <MenuItem key="edit" textValue="Edit">
                          <EditIcon />
                          Edit
                        </MenuItem>
                        <MenuItem key="delete" textValue="Delete">
                          <TrashIcon />
                          Delete
                        </MenuItem>
                        <MenuItem key="resolve" textValue="Resolve">
                          {Boolean(todo.completed_at) ? (
                            <>
                              <RestoreIcon className={Styles.restoreIcon} />
                              Restore
                            </>
                          ) : (
                            <>
                              <ResolveIcon className={Styles.resolveIcon} />
                              Resolve
                            </>
                          )}
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </DropdownTrigger>
              </Item.Control>
            )}
            {canToggleResolve && inCleanupMode && <ResolveControl todo={todo} />}
          </Item.ControlGroup>
        </div>
      )}
      {isEditing ? (
        <TodoItemEditForm todo={todo} initialFocus={initialEditFocus} />
      ) : (
        <>
          <div className={Styles.body}>
            <div className={Styles.titleContainer}>
              <TodoCheckbox
                className={Styles.checkbox}
                checked={!!todo.completed_at}
                onClick={handleCheckboxClick}
                readOnly={!auth}
              />
              {todo.title ? (
                <ProseDisplay html={todo.html_title} className={Styles.title} />
              ) : (
                <span onClick={editTitle} className={Styles.emptyTitle}>
                  Task is empty
                </span>
              )}
            </div>
          </div>
          {(showUpperFooter || showLowerFooter) && (
            <div className={cx(Styles.footer, { [Styles.assigneeSet]: todo.assignee })}>
              {showUpperFooter && (
                <div className={Styles.upperFooter}>
                  {((isActive && canAssign) || todo.assignee) && (
                    <TodoAssigneePicker
                      className={Styles.assignee}
                      value={todo.assignee}
                      onChange={(assignee) => rootStore.todos.update(todo.id, { assigneeId: assignee?.id || null })}
                      size={todo.assignee ? 'sm' : 'xs'}
                      readOnly={!canAssign}
                    />
                  )}
                  {((isActive && canChangeDueDate) || todo.due_date) && (
                    <span className={Styles.dueDatePicker}>
                      <DueDatePicker
                        value={todo.due_date}
                        hideChevron
                        frontIcon={CalendarIcon}
                        onChange={(dueDate) => rootStore.todos.update(todo.id, { dueDate })}
                        size="xs"
                        readOnly={!canChangeDueDate}
                      />
                    </span>
                  )}
                </div>
              )}
              {isActive && todo.description && (
                <ProseDisplay className={Styles.description} html={todo.html_description} />
              )}
            </div>
          )}
        </>
      )}
    </Item.Root>
  )
})

export { TodoItem }
